import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '@/lib/api'
import { useAuth } from '@/components/AuthProvider'

export interface DemoStatus {
  isDemo: boolean
  accountStatus: 'active' | 'pending_approval' | 'locked' | 'rejected' | string
  analysesUsed: number
  analysesLimit: number | null
  expiresAt: string | null
  daysLeft: number | null
  blocked: boolean
  // 'subscription' = a paid account whose subscription ended; the demo reasons
  // only ever apply to isDemo accounts.
  blockReason: 'analyses' | 'expired' | 'pending' | 'rejected' | 'subscription' | null
}

export interface MeResponse {
  id: string
  email: string
  name: string | null
  role: string
  company: string | null
  demo: DemoStatus
}

/**
 * Server-authoritative account status (GET /api/auth/me). Used by the account
 * gate (pending/locked screens) and the demo usage banner. Kept fresh but not
 * chatty: refetches on window focus so an admin approval / lockout reflects
 * within a focus cycle without a manual reload.
 */
export function useDemoStatus() {
  const { user } = useAuth()
  const query = useQuery<MeResponse>({
    queryKey: ['me', user?.id],
    enabled: !!user?.id,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const res = await apiFetch('/api/auth/me', { credentials: 'include' })
      if (!res.ok) throw new Error(`me failed: ${res.status}`)
      return res.json() as Promise<MeResponse>
    },
  })
  return {
    me: query.data,
    demo: query.data?.demo,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  }
}
